import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
} from 'react'
import { CardProduct } from '~/@types/card'
import { InferReturn } from '~/@types/generic'
import { getSfSearchProducts } from '~/lib/shopify/product/search-products'

export type SearchQueryKey<TAdditionalQueryKeyParams = {}> = [
  string,
  {
    query: string
    hitsPerPage: number
  },
  TAdditionalQueryKeyParams,
]

export interface SearchParams<TAdditionalQueryKeyParams = {}> {
  /** Query key */
  key: string
  /** Query string to search by text. */
  query?: string
  /** Props to add more parameters to the query key such as a search index or more filters.  */
  additionalQueryKeyParams?: TAdditionalQueryKeyParams
}

export type TSearchData = InferReturn<typeof getSfSearchProducts> & {
  serializedProductCards: CardProduct[]
  nbHits: number
}

export interface TSearchContext {
  queryKey: string
  hitsPerPage: number
  query: string
  initialData: TSearchData | null
}

export const SearchContext = createContext<TSearchContext>({
  query: '',
  queryKey: '',
  initialData: null,
  hitsPerPage: 0,
})

export function useShopifySearchContext() {
  return useContext(SearchContext)
}

export type ShopifySearchProviderProps<TData = {}> = TData & {
  hitsPerPage: number
  queryKey: string
  query: string
  initialData: TSearchData | null
} & PropsWithChildren

export default function ShopifySearchProvider<TObject>({
  hitsPerPage,
  queryKey,
  query,
  initialData,
  children,
}: ShopifySearchProviderProps<TObject>) {
  const value = useMemo(
    () => ({
      hitsPerPage,
      queryKey,
      query,
      initialData,
    }),
    [queryKey, query, hitsPerPage, initialData],
  )

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}
